import { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../App";
import { ROLE_LOAN_OFFICER } from "../accessControl";
import { getInlineLoader } from "../Helpers";
import OneAdmin from "../ones/OneAdmin";
import { MdArrowOutward, MdPersonAddAlt1 } from "react-icons/md";
import { FaUsers } from "react-icons/fa";

export default function View(props) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [filter, setFilter] = useState("");

  async function init() {
    setReady(false);
    await appContext.getAllAdmins();
    setReady(true);
  }

  useEffect(() => {
    init();
  }, []);

  const filteredAdmins = useMemo(() => {
    const list = appContext.allAdmins || [];
    if (!filter.trim()) return list;

    const term = filter.toLowerCase();
    return list.filter(
      (item) =>
        item.fullname?.toLowerCase().includes(term) ||
        item.phone?.toLowerCase().includes(term) ||
        item.role?.toLowerCase().includes(term)
    );
  }, [filter, appContext.allAdmins]);

  if (!ready) {
    return <div className="container mSupportLoading">{getInlineLoader()}</div>;
  }

  const admins = appContext.allAdmins || [];
  const loanOfficers = admins.filter((item) => item.role === ROLE_LOAN_OFFICER || item.role === "loan_officer").length;
  const cashiers = admins.filter((item) => item.role === "cashier").length;

  const quickLinks = [
    {
      title: "Register administrator",
      text: "Create a new internal account and assign the appropriate operational role.",
      action: "Open form",
      onClick: () => appContext.navTo({ item: "view", subItem: "create-admin" }),
      icon: <MdPersonAddAlt1 />,
    }
  ];

  return (
    <div className="container">
      <div className="app-page">
        <div className="app-page-hero">
          <div>
            <h6 className="main-section-title">Administrator workspace</h6>
            <p className="app-page-subtitle">
              Manage internal user accounts, role assignments, and operational access from one clean control panel.
            </p>
          </div>
        </div>

        <div className="app-form-summary-grid">
          <div className="app-form-summary-card">
            <span>Total administrators</span>
            <strong>{admins.length}</strong>
          </div>

          <div className="app-form-summary-card">
            <span>Loan officers</span>
            <strong>{loanOfficers}</strong>
          </div>
        </div>

        <div className="detail-actions-card my-4">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-4">
            <div>
              <h3 className="card-title mb-1 font-bold">Team members</h3>
              <p className="text-muted small mb-0">Search administrators by name, phone number, or role.</p>
            </div>

            <button
              className="btn btn-primary btn-rounded"
              onClick={() => appContext.navTo({ item: "view", subItem: "create-admin" })}
            >
              <MdPersonAddAlt1 size={18} />
              Register admin
            </button>
          </div>

          <div className="row align-items-end mb-3">
            <div className="col-md-8">
              <div className="app-form-input-wrap">
                <label className="form-label">Search administrators</label>
                <input
                  className="form-control"
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Search by name, phone number, or role"
                  value={filter}
                />
              </div>
            </div>

            <div className="col-md-4 text-md-end mt-3 mt-md-0">
              {filter ? (
                <button className="btn btn-outline-primary btn-rounded" onClick={() => setFilter("")}>
                  Clear filter
                </button>
              ) : (
                <div className="text-muted small d-flex align-items-center justify-content-md-end gap-2">
                  <FaUsers size={14} />
                  Showing {filteredAdmins.length} account(s)
                </div>
              )}
            </div>
          </div>

          {filteredAdmins.length > 0 ? (
            <div className="d-flex flex-column gap-3">
              {filteredAdmins.map((item, i) => (
                <OneAdmin key={item.id || i} data={item} />
              ))}
            </div>
          ) : (
            <div className="empty-state-lite">No administrator records match the current filter.</div>
          )}
        </div>

        <div className="row mt-1">
          <div className="col-md-12">
            <div className="insight-card insight-card-primary px-3 py-4">
              <div className="insight-card-header">
                <div>
                  <p className="app-page-eyebrow">Team visibility</p>
                  <h6 className="section-title">Access overview</h6>
                </div>
              </div>

              

              <div className="insight-grid">
                <div className="insight-metric">
                  <span className="insight-metric-label">Registered admins</span>
                  <strong>{admins.length}</strong>
                </div>
                <div className="insight-metric">
                  <span className="insight-metric-label">Loan officers</span>
                  <strong>{loanOfficers}</strong>
                </div>
                <div className="insight-metric">
                  <span className="insight-metric-label">Cashiers</span>
                  <strong>{cashiers}</strong>
                </div>
              </div>
            </div>
          </div>

          

          <div className="col-lg-5 d-none">
            <div className="insight-card">
              <div className="insight-card-header">
                <div>
                  <h6 className="section-title">Quick actions</h6>
                </div>
              </div>

              <div className="quick-link-list">
                {quickLinks.map((item) => (
                  <button key={item.title} className="quick-link-card" onClick={item.onClick}>
                    <span className="quick-link-copy">
                      <strong>{item.title}</strong>
                      <small>{item.text}</small>
                    </span>
                    <span className="quick-link-action">
                      {item.action}
                      <MdArrowOutward />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        
      </div>
    </div>
  );
}
